import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import type { ProblemAddressResponse } from '@/core/generated/api';
import { formatDateTime } from '@/core/format';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { useVerifyAddressPoint } from '@/features/verifyAddressPoint/model/useVerifyAddressPoint';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { CoordinateField } from '@/shared/ui/CoordinateField/CoordinateField';
import { createRHFFields } from '@/shared/ui/RHFFields/RHFFields';
import { Section } from '@/shared/ui/Section/Section';
import { RequestError } from '@/shared/ui/RequestState';
import {
    getAddressMapLinks,
    getCoordinateDistance,
    getCoordinateMapLink,
    getHouseCoordinates,
} from '@/entities/ProblemAddress/mappers';
import type { AddressPointFormValues } from '@/entities/ProblemAddress/model/schema';
import { AddressPointMap } from '@/entities/ProblemAddress/ui/AddressPointMap';
import classes from './AddressPointEditor.module.scss';

interface AddressPointEditorProps {
    point: ProblemAddressResponse;
    onSaved: (point: ProblemAddressResponse, openNext: boolean) => Promise<void>;
    onStateChange?: (dirty: boolean, busy: boolean) => void;
}

const { TextField } = createRHFFields<AddressPointFormValues>();

/** Позволяет оператору проверить и сохранить координаты дома */
export const AddressPointEditor = ({ point, onSaved, onStateChange }: AddressPointEditorProps) => {
    const canVerify = useCanAccessPermission('admin.problem-addresses.verify');
    const { form, proposed, coordinatesChanged, selectPoint, applyPair, confirmCurrent, save, saveAndNext } = useVerifyAddressPoint(point, onSaved);
    const { isSubmitting: busy, isDirty, errors } = form.formState;
    useEffect(() => { onStateChange?.(isDirty, busy); }, [isDirty, busy, onStateChange]);
    const current = getHouseCoordinates(point);
    const links = getAddressMapLinks(point);

    const distance = current && proposed
        ? getCoordinateDistance(current, proposed)
        : null;

    return <>
        <div className={classes.editor}>
            <Section title="Координаты дома">
                <p>Исходная точность DaData: <strong>{point.qcGeo?.name ?? 'Неизвестна'}</strong></p>
                <p>{point.verifiedAt
                    ? `Проверено оператором ${formatDateTime(point.verifiedAt)}`
                    : 'Требует проверки оператором'}</p>
                <p>Текущие координаты: {point.latitude ?? '—'}, {point.longitude ?? '—'}</p>
                <AddressPointMap
                    current={current}
                    proposed={proposed}
                    onSelect={canVerify && !busy
                        ? selectPoint
                        : undefined
                    }
                />
                <div className={classes.links}>
                    <a
                        href={links.yandex}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Найти адрес в Яндекс Картах ↗
                    </a>
                    <a
                        href={links.twoGis}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Найти адрес в 2ГИС ↗
                    </a>
                    {current &&
                        <a
                            href={getCoordinateMapLink(current)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Открыть текущую точку ↗
                        </a>
                    }
                </div>
            </Section>
            {canVerify &&
                <Section title="Ручная проверка">
                    <p>Проверьте город, улицу, номер дома и корпус. Исправление общей точки применяется ко всем связанным адресам.</p>
                    <FormProvider {...form}>
                        <form onSubmit={(event) => void save(event)}>
                            <div className={classes.pasteFields}>
                                <TextField
                                    name="coordinatePair"
                                    label="Координаты: широта, долгота"
                                    placeholder="55.755864, 37.617698"
                                    disabled={busy}
                                />
                                <ActionButton
                                    type="button"
                                    label="Подставить координаты"
                                    outlined
                                    onClick={applyPair}
                                    disabled={busy}
                                />
                            </div>
                            <div className={classes.coordinateFields}>
                                <CoordinateField<AddressPointFormValues>
                                    name="latitude"
                                    label="Широта"
                                    integerDigits={2}
                                    disabled={busy}
                                />
                                <CoordinateField<AddressPointFormValues>
                                    name="longitude"
                                    label="Долгота"
                                    integerDigits={3}
                                    disabled={busy}
                                />
                            </div>
                            <p>Точка подставляется автоматически. Долгота вводится с тремя цифрами до точки: 037.6176980. Для отрицательных координат нажмите «−»</p>
                            {distance !== null &&
                                <p>Смещение от текущей точки: {distance.toLocaleString('ru-RU')} м</p>
                            }
                            {errors.root?.server?.message &&
                                <RequestError message={errors.root.server.message} />
                            }
                            <div className={classes.actions}>
                                <ActionButton
                                    type="submit"
                                    label="Сохранить координаты"
                                    icon="pi pi-check"
                                    loading={busy}
                                    disabled={busy || !coordinatesChanged}
                                />
                                <ActionButton
                                    type="button"
                                    label="Сохранить и открыть следующий"
                                    outlined
                                    onClick={() => void saveAndNext()}
                                    disabled={busy || !coordinatesChanged}
                                />
                                <ActionButton
                                    type="button"
                                    label="Подтвердить текущие координаты"
                                    outlined
                                    onClick={() => void confirmCurrent()}
                                    disabled={busy || !current}
                                />
                            </div>
                        </form>
                    </FormProvider>
                </Section>
            }
        </div>
        <Section title="История проверки">
            {(point.history?.length ?? 0) === 0
                ? <p>Ручных проверок пока нет</p>
                : <ol className={classes.history}>
                    {point.history?.map((item) => <li key={item.id}>
                        <strong>{formatDateTime(item.verifiedAt)}</strong>
                        <div>{item.previousLatitude ?? '—'}, {item.previousLongitude ?? '—'} → {item.latitude}, {item.longitude}</div>
                        <div>Сотрудник: {item.operatorId ?? 'Локальный разработчик'}</div>
                    </li>)}
                </ol>}
        </Section>
    </>;
};
